// MACHINERY V2 (Stroje) — the fleet SHELL (M2.3; hoisted out of FleetV1Page).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2 + M2.3 + M2.13 + M2.14.
// This is the `""` child of the v2 route.
//
// >>> WHY A SHELL EXISTS: ONE POLLER PER PAGE. <<<
// M2.2 put the data hook inside FleetV1Page. If M2.3 had done the same in
// FleetV2Page, the V1/V2 toggle would have owned two independent
// `useFleetTelemetry` instances — two 60 s intervals, two batch reads of
// /v1/gateway/machines, and a refetch on every toggle. So the hook is hoisted
// HERE and every view becomes a BODY that receives its output as props.
// `useFleetPageData` (and therefore `useFleetTelemetry`) is called exactly once
// on this surface. Do not call either from a body component — that now covers
// FOUR bodies (V1 cards, V2 rows, M2.13's V3 table, M2.14's V4 wall).
//
// Everything else the views share also lives here, for the same reason:
// search, scope, dialogs, the readiness popup and the toast host. Switching
// view re-renders a body and nothing else — no refetch, no reset of the user's
// filters, search or scope.
//
// SPLIT TWICE. At M2.13 the scroll area moved to FleetContent.tsx and the
// page-level overlays to FleetOverlays.tsx. At M2.14's fix pass the whole CHROME
// composition (rail + toolbar + scroll area) moved to FleetShell.tsx — this file
// is now hooks, handlers and the two page-level branches, and nothing else.
//
// ============================================================
//  THE KIOSK BRANCH (M2.14) AND ITS THREE ESCAPES
// ============================================================
// V4, the shop-floor wall, is a KIOSK — no rail, no toolbar (README §V4 /
// ticket M2.14) — so it branches out BELOW every hook and ABOVE the chrome
// rather than joining the branch inside FleetContent. Placing it after the
// hooks is what keeps the wall on the single poller.
//
// Removing the toolbar also removes the view toggle, and `fleetView` is
// localStorage-persisted (M2.13 ruling 3) — so without an escape a laptop user
// who clicks the wall glyph is stuck across reloads. Three escapes, none of
// which puts chrome on the TV: Esc (1, below), `?view=V1` on the URL (2,
// below), and a hint chip that renders only on real pointer movement so a
// pointer-less TV never sees it (3, wall/useWallExitHint.ts).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import { Machine } from "../../models/models";
import { getMachines } from "../../customerMachinerySlice";
import { parseFleetView } from "../machineryV2Persistence";
import { MachineryV2GroupBy, setFleetGroupBy, setFleetView } from "../machineryV2UiSlice";
import FleetOverlays from "./FleetOverlays";
import FleetShell from "./FleetShell";
import { FleetScope } from "./FleetToolbar";
import FleetWallView from "./wall/FleetWallView";
import { fleetFetchQuery } from "./fleetFetchQuery";
import { useFleetDialogs } from "./useFleetDialogs";
import { useFleetHandlers } from "./useFleetHandlers";
import { useFleetSearch } from "./useFleetSearch";
import { useFleetPageData } from "./useFleetPageData";
import { DEFAULT_FLEET_TABLE_SORT, FleetTableSortColumn, nextSort } from "./table/fleetTableSort";

const FleetPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // canAdmin gates ONE row: the decommissioned hard delete (MDEL-FE).
  const { canWrite, canAdmin } = usePermission(Modules.Machinery);

  const query = useAppSelector((s) => s.customerMachinery.query);
  const selectedMachine = useAppSelector((s) => s.customerMachinery.machine);
  const ui = useAppSelector((s) => s.machineryV2Ui);

  const [scope, setScope] = useState<FleetScope>("active");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [readinessMachine, setReadinessMachine] = useState<Machine | null>(null);
  /** M2.7: the v2 add flow (picker -> custom form). See onAddMachine below. */
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  /**
   * M2.13 — the V3 table's column sort. SESSION state, held HERE and not in the
   * table, for the same reason `collapsedGroups` is: a body unmounts on every
   * view toggle, so sort state living inside the table would silently reset on
   * a V3 -> V1 -> V3 round trip. It is deliberately NOT in the slice either —
   * ruling (iii) persists the VIEW MODE only; filters and orderings stay
   * session-only. Reset semantics: see changeGroupBy.
   */
  const [tableSort, setTableSort] = useState(DEFAULT_FLEET_TABLE_SORT);
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * >>> THE WALL RUNS ON THE ACTIVE SCOPE, WHATEVER THE USER LAST PICKED. <<<
   *
   * The `scope` STATE is left untouched — this is a DERIVED value, so the
   * user's Vyradené selection is still there when they leave the wall. Forcing
   * it BEFORE the data hook also matters mechanically: the decommissioned scope
   * passes an empty id set to useFleetTelemetry, which then skips the poll
   * entirely, so a wall entered from that scope would be a grid of dead tiles.
   */
  const isWall = ui.fleetView === "V4";
  const effectiveScope: FleetScope = isWall ? "active" : scope;

  // THE single data hook — and therefore the single telemetry poller.
  const data = useFleetPageData(effectiveScope);
  // The second argument is the catalog add form's way BACK to the picker
  // (M2.8) — the picker's open flag is this page's, so re-opening it is too.
  const dialogs = useFleetDialogs(query, () => setAddPickerOpen(true));
  // Debounced search + the always-current query ref (review F5).
  const { searchValue, onSearchChange, onSearchSubmit, queryRef } = useFleetSearch(query);
  // Named fields, not two adjacent booleans — a swap here used to type-check
  // clean and would have shown the hard delete to every Manager (review
  // finding 3).
  const { buildActions } = useFleetHandlers(
    { canWrite, canAdmin },
    queryRef,
    dialogs,
    navigate
  );

  // NEW badge (session-only), from ?newMachine= after a catalog-page redirect.
  useEffect(() => {
    const newId = searchParams.get("newMachine");
    if (!newId) return;
    dialogs.setNewMachineId(newId);
    searchParams.delete("newMachine");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ESCAPE 2 — `?view=V1` (and its sibling `?view=V4`). Validated through the
   * SAME closed list localStorage reads go through, so a typo in a pasted URL
   * cannot put the fleet into a view no branch renders. The param is then
   * consumed (replace, not push) so it does not sit in the URL fighting the
   * next toggle, and so Back does not re-apply it.
   */
  const viewParam = searchParams.get("view");
  useEffect(() => {
    const parsed = parseFleetView(viewParam);
    if (!parsed) return;
    dispatch(setFleetView(parsed));
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewParam]);

  /** ESCAPE 1 — what Esc does on the wall. V1 is the default view, and
   *  `setFleetView` writes localStorage, so this un-sticks the persisted trap
   *  rather than papering over it for a single session. */
  const exitWall = useCallback(() => dispatch(setFleetView("V1")), [dispatch]);

  // Collapse state is keyed by the RAW section key and is shared between the
  // views — since ruling (i) they all section from the SAME `fleetGroupBy` map,
  // so a group collapsed in V1 is the same group in V2 and V3. Reset on a
  // group-by change (see changeGroupBy).
  const toggleGroup = useCallback((name: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // M2.4 repointed this from the LIVE profile to the v2 one. Card and row
  // clicks now stay on the v2 surface; the live profile is untouched and still
  // reachable at /machinery/:id until cutover (M2.12).
  const openProfile = useCallback(
    (m: Machine) => navigate(`/machinery-v2/${m.id}`),
    [navigate]
  );

  /**
   * REVIEW F2 — changing the dimension MUST clear the collapse set, exactly as
   * the live grid does (Machinery.tsx:759-761). The set is keyed by section
   * TITLE, and groupMachinesClientSide emits the same sentinel `"Ungrouped"`
   * for location, line AND department — so collapsing "Ungrouped" under
   * Group-by Line and switching to Group-by Location would render a DIFFERENT
   * section already collapsed with no user action. Any group name shared
   * across two dimensions collides the same way.
   *
   * M2.13 — THE TABLE SORT RESETS ON THE SAME EVENT, deliberately. Sorting is
   * per-section (each group is its own <table>), so an ordering the user chose
   * is an ordering of sections that no longer exist after a dimension change.
   * Binding both resets to this one event keeps ONE mental model — "changing
   * how the fleet is divided resets how it is arranged" — instead of two
   * half-remembered ones. Everything else preserves the sort: filters, search,
   * scope, the 60 s telemetry tick and a V1/V2/V3 round trip all leave it
   * alone, because that is the ordering the user is actively reading.
   */
  const changeGroupBy = useCallback(
    (g: MachineryV2GroupBy) => {
      dispatch(setFleetGroupBy(g));
      setCollapsedGroups(new Set());
      setTableSort(DEFAULT_FLEET_TABLE_SORT);
    },
    [dispatch]
  );

  const onSortChange = useCallback(
    (column: FleetTableSortColumn) => setTableSort((prev) => nextSort(prev, column)),
    []
  );

  /**
   * KIOSK BRANCH (M2.14). Hook-safe: every hook above has already run, so the
   * 60 s poll keeps ticking and the wall consumes the SAME single
   * useFleetPageData call.
   *
   * >>> IT IS HANDED `data.flat`, NOT `data.visibleFlat`. <<< `visibleFlat` has
   * already been narrowed by the status filters, the location filter and the
   * type/manufacturer facets — every one of them set by chrome the kiosk then
   * REMOVES. Filter to `alarm`, click the wall glyph, walk away, and the hall
   * TV shows a self-consistent "plant overview" of three machines while the
   * plant has five. `flat` is the FACET BASE set — the same set the rail's own
   * counts are computed over (useFleetPageData.ts, `flat` and
   * `countStates(flat, states)`, re-read at commit time) — so the wall renders
   * the whole active park, and its header counts the whole active park.
   *
   * A per-hall wall is a legitimate FUTURE request: it rides the `?view=`
   * escape above as `?view=V4&location=…`, explicitly and linkably. What it
   * must never be is an invisible inheritance from the last thing a user
   * clicked on a surface that then hides the control.
   */
  if (isWall) {
    return (
      <FleetWallView
        machines={data.flat}
        states={data.states}
        telemetry={data.telemetry}
        lastTelemetryOkAt={data.lastTelemetryOkAt}
        isLoading={data.isLoading}
        onExit={exitWall}
      />
    );
  }

  const readinessPresentation = readinessMachine
    ? data.readiness.get(readinessMachine.id)
    : undefined;

  const bodyProps = {
    states: data.states,
    telemetry: data.telemetry,
    readiness: data.readiness,
    newMachineId: dialogs.newMachineId,
    collapsedGroups,
    onToggleGroup: toggleGroup,
    buildActions,
    onOpenProfile: openProfile,
    onOpenReadiness: setReadinessMachine,
  };

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0 }}>
      <FleetShell
        data={data}
        scope={scope}
        onScopeChange={setScope}
        search={searchValue}
        onSearchChange={onSearchChange}
        onSearchSubmit={onSearchSubmit}
        sort={tableSort}
        onSortChange={onSortChange}
        onGroupByChange={changeGroupBy}
        canWrite={canWrite}
        onManageGroups={dialogs.openManageGroups}
        onAddMachine={() => setAddPickerOpen(true)}
        body={bodyProps}
      />

      <FleetOverlays
        readinessMachine={readinessMachine}
        readinessPresentation={readinessPresentation}
        onCloseReadiness={() => setReadinessMachine(null)}
        addPickerOpen={addPickerOpen}
        onCloseAddPicker={() => setAddPickerOpen(false)}
        onCreated={(id) => {
          setAddPickerOpen(false);
          if (id) dialogs.setNewMachineId(id);
          dispatch(getMachines(fleetFetchQuery(queryRef.current)));
        }}
        onCatalogModel={dialogs.openCatalogAdd}
        selectedMachine={selectedMachine}
        dialogProps={dialogs.dialogProps}
      />
    </div>
  );
};

export default FleetPage;
